import * as React from "react";
import { cn } from "../../lib/utils";
import { ShadcnButton } from "../ShadcnButton/ShadcnButton";
import { Input } from "../ui/input";

export interface CodebaseIssue {
  id: string;
  title: string;
  description: string;
  severity: "low" | "medium" | "high" | "critical";
  category: string;
  files: string[];
  suggestedPrompt: string;
  location?: string;
}

export interface AnalysisCard {
  id: string;
  status: "analyzing" | "complete";
  issue?: CodebaseIssue;
}

export interface CodebaseAnalyzerProps {
  cards?: AnalysisCard[];
  onPromptCopy?: (prompt: string, issueId: string) => void;
  onAnalyze?: (repoUrl: string, token: string) => void;
  initialRepoUrl?: string;
  initialToken?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

const placeholderMessages = [
  "Zigging the zag...",
  "Solving world peace...",
  "Reading your mind...",
  "Consulting the algorithms...",
  "Finding the edge cases...",
  "Optimizing the flux capacitor...",
  "Herding the cats...",
  "Untangling the spaghetti...",
  "Reticulating splines...",
  "Brewing coffee...",
  "Counting backwards from infinity...",
  "Dividing by zero...",
  "Reversing entropy...",
  "Questioning existence...",
  "Achieving enlightenment...",
];

const severityBadgeColors = {
  low: "bg-[#A49CED]/10 text-[#A49CED] border-[#A49CED]/20",
  medium: "bg-[#EDC29C]/10 text-[#EDC29C] border-[#EDC29C]/20",
  high: "bg-[#EDC29C]/20 text-[#EDC29C] border-[#EDC29C]/40",
  critical: "bg-[#ED9C9C]/10 text-[#ED9C9C] border-[#ED9C9C]/20",
};

const AnalyzingCard: React.FC = () => {
  const [messageIndex, setMessageIndex] = React.useState(
    Math.floor(Math.random() * placeholderMessages.length)
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % placeholderMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="group relative flex aspect-square items-center justify-center overflow-hidden rounded-xl border border-white/10 text-white shadow"
      style={{
        backgroundColor: "rgba(235, 220, 207, 0.045)",
        backdropFilter: "blur(1000px)",
        WebkitBackdropFilter: "blur(1000px)",
      }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.06),transparent_30%)] opacity-70 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="absolute inset-0 animate-pulse bg-white/[0.03]" />
      <div className="relative flex flex-col items-center gap-3 text-xs text-white/70">
        <div className="h-5 w-5 rounded-full border-2 border-[#EDC29C]/30 border-t-[#EDC29C] animate-spin" />
        <p className="max-w-[140px] text-center text-[11px] font-medium tracking-wide">
          {placeholderMessages[messageIndex]}
        </p>
      </div>
    </div>
  );
};

const IssueCard: React.FC<{
  issue: CodebaseIssue;
  onPromptCopy?: (prompt: string, issueId: string) => void;
}> = ({ issue, onPromptCopy }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard.writeText(issue.suggestedPrompt);
    setCopied(true);
    onPromptCopy?.(issue.suggestedPrompt, issue.id);
    setTimeout(() => setCopied(false), 2000);
  }, [issue, onPromptCopy]);

  return (
    <div
      className={cn(
        "group relative flex aspect-square flex-col overflow-hidden rounded-xl border border-white/10 text-white shadow p-4"
      )}
      style={{
        backgroundColor: "rgba(235, 220, 207, 0.045)",
        backdropFilter: "blur(1000px)",
        WebkitBackdropFilter: "blur(1000px)",
      }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(255,255,255,0.08),transparent_35%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.06),transparent_30%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-hover:bg-white/5" />

      <div className="relative mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-white/60">
          <span className="h-2.5 w-2.5 rounded-full bg-white/60" />
          <span className="line-clamp-1 font-medium">{issue.category}</span>
        </div>
        <span
          className={cn(
            "flex-shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-tight",
            severityBadgeColors[issue.severity]
          )}
        >
          {issue.severity}
        </span>
      </div>

      <div className="relative mb-2 space-y-1">
        <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-[#FAF1E9]">
          {issue.title}
        </h3>
        <p className="line-clamp-3 text-xs leading-relaxed text-[#FAF1E9]/70">
          {issue.description}
        </p>
      </div>

      <div className="relative mb-3 flex-1">
        <p className="mb-1 text-[11px] uppercase tracking-[0.12em] text-white/50">
          Files ({issue.files.length})
        </p>
        <div className="space-y-1 text-[11px] text-white/70">
          {issue.files.slice(0, 2).map((file, idx) => (
            <p key={idx} className="truncate font-mono">
              {file}
            </p>
          ))}
          {issue.files.length > 2 && (
            <p className="text-white/50">+{issue.files.length - 2} more</p>
          )}
        </div>
      </div>

      <button
        onClick={handleCopy}
        className="relative mt-auto inline-flex items-center justify-center gap-2 rounded-lg border border-[#EDC29C]/40 bg-transparent px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#EDC29C] shadow-sm hover:bg-[#EDC29C]/10 hover:border-[#EDC29C]/60 active:bg-[#EDC29C]/20 transition"
      >
        {copied ? (
          <>
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Copied
          </>
        ) : (
          <>
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copy prompt
          </>
        )}
      </button>
    </div>
  );
};

export const CodebaseAnalyzer = React.forwardRef<
  HTMLDivElement,
  CodebaseAnalyzerProps
>(
  (
    {
      cards = [],
      onPromptCopy,
      onAnalyze,
      initialRepoUrl = "",
      initialToken = "",
      title = "Codebase Analyzer",
      subtitle = "Add your repository and PAT to watch issues surface in real time.",
      className,
    },
    ref
  ) => {
    const [repoUrl, setRepoUrl] = React.useState(initialRepoUrl);
    const [token, setToken] = React.useState(initialToken);

    React.useEffect(() => {
      setRepoUrl(initialRepoUrl);
    }, [initialRepoUrl]);

    React.useEffect(() => {
      setToken(initialToken);
    }, [initialToken]);

    const handleAnalyze = React.useCallback(() => {
      if (!onAnalyze) return;
      onAnalyze(repoUrl.trim(), token.trim());
    }, [onAnalyze, repoUrl, token]);

    const handleSubmit = React.useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleAnalyze();
      },
      [handleAnalyze]
    );

    const gridCards = React.useMemo(() => {
      const desiredCount = Math.max(cards.length, 16);
      const result = [...cards];
      while (result.length < desiredCount) {
        result.push({
          id: `empty-${result.length}`,
          status: "analyzing" as const,
        });
      }
      return result;
    }, [cards]);

    const completedCount = cards.filter((c) => c.status === "complete").length;
    const analyzingCount = cards.filter((c) => c.status === "analyzing").length;

    return (
      <div
        ref={ref}
        className={cn(
          "relative w-full overflow-hidden rounded-xl border border-white/10 text-white shadow p-6 sm:p-8",
          className
        )}
        style={{
          backgroundColor: "rgba(235, 220, 207, 0.045)",
          backdropFilter: "blur(1000px)",
          WebkitBackdropFilter: "blur(1000px)",
        }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-80">
          <div className="absolute -left-28 -top-32 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-56 w-56 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.05),transparent_45%)]" />
        </div>

        <div className="relative space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/60">
                Repository Audit
              </p>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl text-[#FAF1E9]">
                {title}
              </h2>
              <p className="text-sm text-[#FAF1E9]/70">{subtitle}</p>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white/70">
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#9CEDC7]" />
                {completedCount} found
              </span>
              <span className="h-3 w-px bg-white/10" />
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#EDC29C]" />
                {analyzingCount} scanning
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_auto] md:items-end">
              <div className="group">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
                  Repository URL
                </label>
                <div className="mt-1">
                  <Input
                    placeholder="https://github.com/acme/repo"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                  />
                </div>
              </div>

              <div className="group">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
                  GitHub PAT
                </label>
                <div className="mt-1">
                  <Input
                    placeholder="ghp_xxxxxxxxxxxxxxxxxx"
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                  />
                </div>
              </div>

              <ShadcnButton
                type="submit"
                variant="outline"
                className="h-10"
              >
                Analyze
              </ShadcnButton>
            </div>
            <p className="text-[11px] text-white/50">
              We only use your token to reach GitHub for this audit. Remove or
              rotate it anytime.
            </p>
          </form>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">
            {gridCards.map((card) => {
              if (card.status === "complete" && card.issue) {
                return (
                  <IssueCard
                    key={card.id}
                    issue={card.issue}
                    onPromptCopy={onPromptCopy}
                  />
                );
              }
              return <AnalyzingCard key={card.id} />;
            })}
          </div>
        </div>
      </div>
    );
  }
);

CodebaseAnalyzer.displayName = "CodebaseAnalyzer";
